// components/profile/SSNVerification.tsx
import React from "react";
import {
  CreditCard,
  CheckCircle,
  Clock,
  AlertTriangle,
  ArrowRight,
  Shield,
} from "lucide-react";

interface SSNVerificationProps {
  status: string; // "not_started" | "pending" | "verified" | "failed"
  onStartVerification: () => void;
}

const SSNVerification: React.FC<SSNVerificationProps> = ({
  status,
  onStartVerification,
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case "verified":
        return {
          icon: CheckCircle,
          iconBg: "bg-emerald-100",
          iconColor: "text-emerald-600",
          title: "SSN Verified",
          description:
            "Your Social Security Number has been verified successfully.",
          showButton: false,
          statusBadge: {
            bg: "bg-emerald-50",
            border: "border-emerald-200",
            text: "text-emerald-700",
            label: "Verified",
          },
        };
      case "pending":
        return {
          icon: Clock,
          iconBg: "bg-amber-100",
          iconColor: "text-amber-600",
          title: "Verification Pending",
          description:
            "Your SSN verification is being processed. This usually takes 1-2 business days.",
          showButton: false,
          statusBadge: {
            bg: "bg-amber-50",
            border: "border-amber-200",
            text: "text-amber-700",
            label: "Pending",
          },
        };
      case "failed":
        return {
          icon: AlertTriangle,
          iconBg: "bg-red-100",
          iconColor: "text-red-600",
          title: "Verification Failed",
          description:
            "We couldn't verify your SSN. Please try again or contact support.",
          showButton: true,
          buttonLabel: "Try Again",
          statusBadge: {
            bg: "bg-red-50",
            border: "border-red-200",
            text: "text-red-700",
            label: "Failed",
          },
        };
      default:
        return {
          icon: Shield,
          iconBg: "bg-gray-100",
          iconColor: "text-gray-600",
          title: "SSN Verification Required",
          description:
            "Verify your Social Security Number to receive payments and unlock all features.",
          showButton: true,
          buttonLabel: "Start Verification",
          statusBadge: null,
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-xl ${config.iconBg} flex items-center justify-center`}
          >
            <CreditCard className={`w-5 h-5 ${config.iconColor}`} />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            SSN Verification
          </h2>
        </div>
        {config.statusBadge && (
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium border ${config.statusBadge.bg} ${config.statusBadge.border} ${config.statusBadge.text}`}
          >
            {config.statusBadge.label}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div
            className={`w-12 h-12 rounded-xl ${config.iconBg} flex items-center justify-center flex-shrink-0`}
          >
            <config.icon className={`w-6 h-6 ${config.iconColor}`} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{config.title}</h3>
            <p className="text-sm text-gray-500 mt-1">{config.description}</p>

            {config.showButton && (
              <button
                onClick={onStartVerification}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 text-sm"
              >
                {config.buttonLabel}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Security Note */}
        <div className="mt-4 p-3 bg-gray-50 rounded-xl flex items-start gap-3">
          <Shield className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-gray-500">
            Your SSN is encrypted and securely stored. We use bank-level
            security to protect your information.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SSNVerification;
